import React, { useMemo } from 'react';
import { createCn } from '../../../helpers/utils';
import { SpeechSegment } from '../../../services/api.service';
import { buildAdvancedMetrics } from '../utils/advanced-metrics';
import {
  assessGini,
  assessOverlaps,
  assessQuestionRate,
  assessWpm,
  ZonedAssessment,
} from '../utils/benchmark-insights';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type SpeakerTimeline = {
  name: string;
  color: string;
};

type Props = {
  segments: SpeechSegment[];
  durationSec: number;
  speakerTimelines: SpeakerTimeline[];
  onSeek: (seconds: number) => void;
};

function MetricTile({
  title,
  value,
  subtitle,
  assessment,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  assessment?: ZonedAssessment;
}) {
  const zone = assessment?.zone;
  return (
    <div className={cn('advanced-metric-tile', zone ? { [zone]: true } : {})}>
      <span className={cn('advanced-metric-tile-title')}>{title}</span>
      <span className={cn('advanced-metric-tile-value')}>{value}</span>
      {subtitle && <span className={cn('advanced-metric-tile-sub')}>{subtitle}</span>}
      {assessment && (
        <span className={cn('advanced-metric-tile-insight', { [assessment.zone]: true })}>
          {assessment.insight}
        </span>
      )}
    </div>
  );
}

const AdvancedMetricsPanel: React.FC<Props> = ({
  segments,
  durationSec,
  speakerTimelines,
  onSeek,
}) => {
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => { map[s.name] = s.color; });
    return map;
  }, [speakerTimelines]);

  const metrics = useMemo(
    () => buildAdvancedMetrics(segments, durationSec, colorMap),
    [segments, durationSec, colorMap],
  );

  if (!metrics) return null;

  return (
    <div className={cn('advanced-metrics')}>
      <div className={cn('advanced-metrics-grid')}>
        <MetricTile
          title="Баланс речи (Gini)"
          value={metrics.gini}
          subtitle={metrics.giniLabel}
          assessment={assessGini(metrics.gini)}
        />
        <MetricTile
          title="Доля вопросов"
          value={`${metrics.questions.ratePct}%`}
          subtitle={`${metrics.questions.total} из ${metrics.questions.replicaCount} реплик`}
          assessment={assessQuestionRate(metrics.questions.ratePct)}
        />
        <MetricTile
          title="Темп речи"
          value={metrics.speechRate.overallWpm}
          subtitle="слов/мин"
          assessment={assessWpm(metrics.speechRate.overallWpm)}
        />
        <MetricTile
          title="Наложения (overlap)"
          value={metrics.overlaps.total}
          subtitle="одновременная речь"
          assessment={assessOverlaps(metrics.overlaps.total, durationSec)}
        />
      </div>

      {metrics.monologues.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>Длинные монологи</p>
          <ul className={cn('advanced-metrics-list')}>
            {metrics.monologues.map(m => (
              <li key={`${m.speaker}-${m.startSec}`}>
                <button type="button" className={cn('advanced-metrics-row-btn')} onClick={() => onSeek(m.startSec)}>
                  <span className={cn('advanced-metrics-dot')} style={{ backgroundColor: m.color }} />
                  <span className={cn('advanced-metrics-row-name')}>{m.displayName}</span>
                  <span className={cn('advanced-metrics-row-meta')}>
                    {m.durationLabel} · {m.replicaCount} репл.
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.overlaps.top.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>Примеры overlap</p>
          <ul className={cn('advanced-metrics-list')}>
            {metrics.overlaps.top.slice(0, 5).map(o => (
              <li key={`${o.atSec}-${o.speakerA}-${o.speakerB}`}>
                <button type="button" className={cn('advanced-metrics-row-btn')} onClick={() => onSeek(o.atSec)}>
                  <span className={cn('advanced-metrics-row-time')}>{o.timeLabel}</span>
                  <span className={cn('advanced-metrics-row-name')}>
                    <span style={{ color: o.colorA }}>{o.speakerA.split(' ')[0]}</span>
                    {' ∩ '}
                    <span style={{ color: o.colorB }}>{o.speakerB.split(' ')[0]}</span>
                  </span>
                  <span className={cn('advanced-metrics-row-meta')}>{o.overlapSec} сек</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.questions.bySpeaker.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>Вопросы по спикерам</p>
          <ul className={cn('advanced-metrics-list')}>
            {metrics.questions.bySpeaker.map(q => (
              <li key={q.speaker}>
                <div className={cn('advanced-metrics-row')}>
                  <span className={cn('advanced-metrics-dot')} style={{ backgroundColor: q.color }} />
                  <span className={cn('advanced-metrics-row-name')}>{q.displayName}</span>
                  <span className={cn('advanced-metrics-row-meta')}>{q.count} · {q.ratePct}%</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.speechRate.bySpeaker.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>Темп речи по спикерам</p>
          <ul className={cn('advanced-metrics-list')}>
            {metrics.speechRate.bySpeaker.map(s => (
              <li key={s.speaker}>
                <div className={cn('advanced-metrics-row')}>
                  <span className={cn('advanced-metrics-dot')} style={{ backgroundColor: s.color }} />
                  <span className={cn('advanced-metrics-row-name')}>{s.displayName}</span>
                  <span className={cn('advanced-metrics-row-meta')}>{s.wpm} сл/мин</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.deadZones.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>«Мёртвые зоны» (паузы ≥25 сек)</p>
          <ul className={cn('advanced-metrics-list')}>
            {metrics.deadZones.map(z => (
              <li key={`${z.startSec}-${z.endSec}`}>
                <button type="button" className={cn('advanced-metrics-row-btn')} onClick={() => onSeek(z.startSec)}>
                  <span className={cn('advanced-metrics-row-time')}>{z.durationLabel}</span>
                  <span className={cn('advanced-metrics-row-meta')}>тишина / не размечено</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.languageMix.length > 0 && (
        <div className={cn('advanced-metrics-block')}>
          <p className={cn('advanced-metrics-block-title')}>KZ / RU mix</p>
          <div className={cn('advanced-metrics-lang-chips')}>
            {metrics.languageMix.map(l => (
              <span key={l.lang} className={cn('advanced-metrics-lang-chip')}>
                {l.label} · {l.pct}%
              </span>
            ))}
          </div>
          {metrics.languageBySpeaker.length > 0 && (
            <ul className={cn('advanced-metrics-list')}>
              {metrics.languageBySpeaker.map(l => (
                <li key={l.speaker}>
                  <div className={cn('advanced-metrics-row')}>
                    <span className={cn('advanced-metrics-dot')} style={{ backgroundColor: l.color }} />
                    <span className={cn('advanced-metrics-row-name')}>{l.displayName}</span>
                    <span className={cn('advanced-metrics-row-meta')}>{l.dominantLang} {l.dominantPct}%</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedMetricsPanel;
